import { useEffect, useState, type ReactNode } from "react";
import {
  HomeframeSelect,
  useRouterSnapshot,
  useHomeframeRouter,
} from "@builtbyted/homeframe";
import { api } from "./api";
export type Row = Record<string, unknown>;
export const num = (v: unknown) => Number(v || 0);
export const str = (v: unknown) => (v == null ? "" : String(v));
export const count = (v: unknown) => num(v).toLocaleString();
export const duration = (v: unknown) => {
  const n = Math.max(0, num(v));
  return n >= 3600
    ? `${Math.floor(n / 3600).toLocaleString()}h ${Math.floor((n % 3600) / 60)}m`
    : `${Math.floor(n / 60)}m`;
};
export const date = (v: unknown) =>
  num(v) ? new Date(num(v) * 1000).toLocaleString() : "—";
export const shortDate = (v: unknown) =>
  num(v) ? new Date(num(v) * 1000).toLocaleDateString() : "—";
export const timeAgo = (v: unknown) => {
  const n = Date.now() / 1000 - num(v);
  return !num(v)
    ? "Never"
    : n < 60
      ? "Just now"
      : n < 3600
        ? `${Math.floor(n / 60)}m ago`
        : n < 86400
          ? `${Math.floor(n / 3600)}h ago`
          : `${Math.floor(n / 86400)}d ago`;
};
export function useData<T>(path: string | null, interval = 0) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);
  useEffect(() => {
    if (!path) return;
    setData(null);
    setError("");
    let active = true;
    let pending = false;
    const load = async () => {
      if (pending) return;
      pending = true;
      try {
        const d = await api<T>(path);
        if (active) {
          setData(d);
          setError("");
        }
      } catch (e) {
        if (active) setError((e as Error).message);
      } finally {
        pending = false;
      }
    };
    void load();
    const timer = interval
      ? setInterval(() => {
          if (document.visibilityState === "visible") void load();
        }, interval)
      : undefined;
    return () => {
      active = false;
      clearInterval(timer);
    };
  }, [path, interval, refreshKey]);
  return { data, error, refresh: () => setRefreshKey((k) => k + 1) };
}
export function useQuery() {
  const route = useRouterSnapshot();
  return new URL(route.url, window.location.origin);
}
export function scoped(
  path: string,
  url: URL,
  extra: Record<string, string> = {},
) {
  const p = new URLSearchParams(
    Object.entries(extra).filter(([, v]) => v !== ""),
  );
  const server = url.searchParams.get("server");
  if (server) p.set("server_id", server);
  return `${path}?${p}`;
}
export function useLink(path: string) {
  const url = useQuery();
  return (
    path +
    (url.searchParams.get("server")
      ? `?server=${url.searchParams.get("server")}`
      : "")
  );
}

export function useNavigateQuery() {
  const router = useHomeframeRouter();
  return (values: Record<string, string>, pathname?: string) => {
    const url = new URL(window.location.href);
    if (pathname) url.pathname = pathname;
    for (const [k, v] of Object.entries(values)) {
      if (v) url.searchParams.set(k, v);
      else url.searchParams.delete(k);
    }
    void router.navigate(url.pathname + url.search);
  };
}
export function Page({
  title,
  tools,
  children,
}: {
  title: string;
  tools?: ReactNode;
  children: ReactNode;
}) {
  return (
    <main className="page">
      <div className="page-title">
        <h1>{title}</h1>
        {tools}
      </div>
      {children}
    </main>
  );
}
export function Message({
  children,
  error = false,
}: {
  children: ReactNode;
  error?: boolean;
}) {
  return (
    <div
      className={error ? "message error" : "message"}
      role={error ? "alert" : "status"}
    >
      {children}
    </div>
  );
}
export function Section({
  title,
  tools,
  children,
}: {
  title: string;
  tools?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="section">
      <div className="padded-header">
        <h2>{title}</h2>
        {tools}
      </div>
      {children}
    </section>
  );
}
export function Poster({
  server,
  path,
  title = "",
  square = false,
}: {
  server: unknown;
  path: unknown;
  title?: string;
  square?: boolean;
}) {
  const [failed, setFailed] = useState(false);
  const p = str(path);
  useEffect(() => setFailed(false), [p]);
  return (
    <div className={`poster${square ? " square" : ""}`}>
      {p && !failed ? (
        <img
          src={`/api/image/${server}?path=${encodeURIComponent(p)}&width=300`}
          alt={title}
          loading="lazy"
          onError={() => setFailed(true)}
        />
      ) : (
        <span aria-hidden="true">▶</span>
      )}
    </div>
  );
}
export function DayPicker() {
  const url = useQuery();
  const navigateQuery = useNavigateQuery();
  return (
    <label className="inline-control">
      Last{" "}
      <HomeframeSelect
        aria-label="Statistics period"
        value={url.searchParams.get("days") || "30"}
        onChange={(e) => navigateQuery({ days: e.target.value })}
      >
        {[7, 30, 90, 365, 36500].map((v) => (
          <option key={v} value={v}>
            {v === 36500 ? "All time" : `${v} days`}
          </option>
        ))}
      </HomeframeSelect>
    </label>
  );
}
export function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="metric">
      <strong>{value}</strong>
      <span>{label}</span>
    </div>
  );
}
export function TopList({
  title,
  rows,
  posters = false,
}: {
  title: string;
  rows?: Row[] | undefined;
  posters?: boolean;
}) {
  return (
    <article className="stats-card">
      <h3>{title}</h3>
      {rows?.slice(0, 5).map((r, i) => (
        <div className="ranking" key={i}>
          <span className="rank">{i + 1}</span>
          {posters && <Poster server={r.server_id} path={r.thumb} />}
          <span className="ranking-label">
            {str(r.label) || "Unknown"}
            <small>{duration(r.duration)}</small>
          </span>
          <strong>
            {count(r.plays)}
            <small>plays</small>
          </strong>
        </div>
      ))}
      {rows?.length === 0 && <p className="subtle">No plays in this period.</p>}
    </article>
  );
}
